import React, { useEffect, useState, useRef, ChangeEvent, useCallback, useMemo } from 'react';
import {
  IconContainer,
  ErrorMessage,
  DropDownHeader,
  DropDownListContainer,
  DropDownList,
  ListItem,
  DownIcon,
  Input,
  Container,
  Label,
  InputGroup,
  PhoneIcon,
} from './PhoneInput.styles';
import { pages } from '../../../defaultVerbiages';
import { useAppSelector } from '@/app/hooks';

type LoanOption = {
  label: string;
  beValue: string;
};

export interface PhoneInputProps {
  dataTestId?: string;
  label: string;
  placeHolder: string;
  options?: LoanOption[];
  hasError?: string;
  onChange?: (option: string, propertyType?: string) => void;
  propertyType?: string | undefined;
  typeOfIcon?: string;
  isOpen?: boolean;
  isIconUp?: boolean;
  hasIcon?: boolean;
  isHidden?: boolean;
  focused?: boolean;
  isAddressIcon?: boolean;
  labelContainer?: boolean;
  type?: string;
  iconColor?: string;
  size?: string;
  hasAsterisk?: boolean;
  propertyName?: string;
  value?: string;
  isRequired?: boolean;
  selectedOption?: string;
  setTypeOfPhone?: React.Dispatch<React.SetStateAction<'mobilePhone' | 'workPhone' | 'homePhone' | 'select'>>;
  setIsErrorForm?: React.Dispatch<React.SetStateAction<boolean>>;
  placeholderColor?: string;
  isSentitiveInfo?: boolean;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  dataTestId,
  onChange,
  propertyType,
  label = '',
  placeHolder = '',
  options = [],
  typeOfIcon,
  isHidden = false,
  labelContainer = false,
  iconColor = '#5140E9',
  size = '20%',
  hasAsterisk = false,
  propertyName = '',
  value = '',
  isRequired = false,
  selectedOption = '',
  setTypeOfPhone,
  setIsErrorForm,
  placeholderColor = '#aaafb7',
  isSentitiveInfo = false,
}) => {
  const [formattedValue, setFormattedValue] = useState(value || '');
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [inputError, setInputError] = useState(false);
  const [maxLength, setMaxLength] = useState<number | undefined>(undefined);
  const hasIcon = typeOfIcon !== undefined;
  const ref = useRef<HTMLDivElement>(null);
  const labelDropDown = pages.LetsGetStarted.consumerInformation.phone.titleDropDown || 'Select';
  const [isInputDisabled, setInputDisabled] = useState(!selectedOption || selectedOption === labelDropDown);
  const [currentOption, setCurrentOption] = useState<string>(selectedOption || labelDropDown);
  const isPersonalInformation = propertyName === 'personal-information';
  const currentForm = useAppSelector((state) => state.CurrentForm);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeout = useRef<NodeJS.Timeout | null>(null);
  const phoneRegex = useMemo(() => /^\(\d{3}\)\s\d{3}-\d{4}$/, []);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const suppressNextEmptyErrorRef = useRef<boolean>(false);
  const templateConfig = useAppSelector((state) => state.TemplateConfig);
  const shouldApplyMasking = isSentitiveInfo && !templateConfig?.pages?.features?.newRelic?.unmaskSensitiveFields;

  const formatPropertySelected = useCallback(
    (value: string) => {
      const phoneTypeMap: { [key: string]: string } = {
        'Mobile Phone': 'mobilePhone',
        'Home Phone': 'homePhone',
        'Work Phone': 'workPhone',
        mobilePhone: 'mobilePhone',
        homePhone: 'homePhone',
        workPhone: 'workPhone',
        cell: 'mobilePhone',
        home: 'homePhone',
        work: 'workPhone',
      };

      if (phoneTypeMap[value]) {
        const mappedValue = phoneTypeMap[value];
        if (setTypeOfPhone) {
          setTypeOfPhone(mappedValue as 'mobilePhone' | 'workPhone' | 'homePhone');
        }
        return mappedValue;
      }

      const words = value.trim().toLowerCase().split(' ');
      const camelCased =
        words[0] +
        words
          .slice(1)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join('');

      if (setTypeOfPhone) {
        setTypeOfPhone(camelCased as 'mobilePhone' | 'workPhone' | 'homePhone');
      }

      return camelCased;
    },
    [setTypeOfPhone],
  );

  const getLabelByBeValue = useCallback(
    (be: string) => {
      const opt = options?.find((o) => o.beValue === be);
      return opt?.label ?? be;
    },
    [options],
  );

  const toggling = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        toggling();
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        if (isOpen && options.length > 0) {
          event.preventDefault();
          const firstOption = document.querySelector(`[data-testid="${dataTestId}-phone-option-0"]`) as HTMLElement;
          if (firstOption) {
            firstOption.focus();
          }
        }
        break;
    }
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent, option: string, optionIndex: number) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        onOptionClicked(option)();
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        event.preventDefault();
        const nextOption = document.querySelector(`[data-testid="${dataTestId}-phone-option-${optionIndex + 1}"]`) as HTMLElement;
        if (nextOption) {
          nextOption.focus();
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (optionIndex === 0) {
          const header = document.querySelector(`[data-testid="${dataTestId}-phone-header"]`) as HTMLElement;
          if (header) {
            header.focus();
          }
        } else {
          const prevOption = document.querySelector(`[data-testid="${dataTestId}-phone-option-${optionIndex - 1}"]`) as HTMLElement;
          if (prevOption) {
            prevOption.focus();
          }
        }
        break;
    }
  };

  const onOptionClicked = useCallback(
    (option: string) => () => {
      const propertySelected = formatPropertySelected(option);

      const isChangingType = currentOption !== option;
      if (isChangingType) {
        setFormattedValue('');
        setInputError(false);
        if (setIsErrorForm) setIsErrorForm(false);
        suppressNextEmptyErrorRef.current = true;
        if (setTypeOfPhone) {
          setTypeOfPhone(propertySelected as 'mobilePhone' | 'workPhone' | 'homePhone');
        }
      }

      setCurrentOption(option);
      setIsOpen(false);
      setInputDisabled(false);

      if (option === 'Select' && formattedValue === '') {
        return setInputError(false);
      }
      if (onChange) {
        setInputError(false);
        if (setIsErrorForm && isRequired) {
          setIsErrorForm(false);
        }
        const valueToEmit = isChangingType ? '' : formattedValue;
        onChange(valueToEmit, propertySelected);
      }
    },
    [onChange, formattedValue, setTypeOfPhone, setIsErrorForm, isRequired, formatPropertySelected, currentOption],
  );

  const handleClickOutside = (event: MouseEvent) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      if (!selectedOption) {
        setIsOpen(false);
      }
    }
    if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
      if (suppressNextEmptyErrorRef.current) {
        suppressNextEmptyErrorRef.current = false;
        return setInputError(false);
      }
      if (currentOption !== 'Select' && formattedValue === '') {
        return setInputError(true);
      }
      return setInputError(false);
    }
  };

  useEffect(() => {
    if (value && propertyType && !isTyping) {
      // only update if there is not a formatted value or current value is empty
      if (!formattedValue || formattedValue === '') {
        const phoneDigits = value.replace(/[^0-9]/g, '');
        const finalValue = phoneRegex.test(value) ? value : formatInput(phoneDigits);
        setFormattedValue(finalValue);
        setCurrentOption(selectedOption !== labelDropDown ? selectedOption : label);
        setInputDisabled(false);
      }
    }
  }, [value, propertyType, selectedOption, label, formattedValue, isTyping, labelDropDown, phoneRegex]);

  useEffect(() => {
    if (value && selectedOption && selectedOption !== labelDropDown && !isTyping) {
      const phoneDigits = value.replace(/[^0-9]/g, '');
      const finalValue = phoneRegex.test(value) ? value : formatInput(phoneDigits);
      setFormattedValue(finalValue);
      setCurrentOption(selectedOption);
      setInputDisabled(false);
    } else if (selectedOption && selectedOption !== labelDropDown && !value && !isTyping) {
      setCurrentOption(selectedOption);
      setInputDisabled(false);
    }
  }, [value, selectedOption, labelDropDown, isTyping, phoneRegex]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = (e: ChangeEvent<HTMLInputElement>) => {
    setFocused(false);
    const rawValue = e.target.value.trim();
    let hasError = false;

    if (isRequired && rawValue === '') {
      hasError = true;
    } else if (rawValue !== '' && !phoneRegex.test(rawValue)) {
      hasError = true;
    } else if (!isRequired && rawValue === '') {
      setFormattedValue('');
      hasError = false;
    }

    setInputError(hasError);

    if (setIsErrorForm && isRequired) {
      setIsErrorForm(hasError);
    } else if (setIsErrorForm && !isRequired) {
      setIsErrorForm(false);
    }
  };

  const formatInput = (input: string): string => {
    const phoneNumber = /^(\d{0,3})(\d{0,3})(\d{0,4})$/;
    const match = input.match(phoneNumber);
    if (match) {
      const [, dig, num1, num2] = match;
      let formattedDate = dig;
      if (num1) {
        formattedDate += num1;
      }
      if (num2) {
        formattedDate += num2;
        formattedDate = `(${dig}) ${num1}-${num2}`;
      }
      return formattedDate;
    }
    return input;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setMaxLength(14);
    setIsTyping(true);
    const inputValue = event.target.value.replace(/[^0-9]/g, '');
    const formatted = formatInput(inputValue);
    setFormattedValue(formatted);

    const isValid = phoneRegex.test(formatted);

    setInputError(!isValid);

    if (setIsErrorForm) {
      setIsErrorForm(!isValid);
      if (!isRequired && inputValue === '') {
        setIsErrorForm(false);
        setInputError(false);
      }
    }

    if (typingTimeout.current) clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      setIsTyping(false);
      if (onChange) {
        const property = currentOption && currentOption !== 'Select' ? formatPropertySelected(currentOption) : propertyType;
        onChange(formatted, property);
      }
    }, 100);
  };

  useEffect(() => {
    // only update from currentForm if not typing, no value prop, and no current formatted value
    if (!isTyping && propertyType && !value && !formattedValue) {
      // for phone inputs, prioritize specific phone type fields over generic phone field
      if (propertyType === 'phone') {
        // don't restore from currentForm.phone if it's an outdated value
        return;
      }

      if (currentForm[propertyType] !== undefined) {
        setFormattedValue(currentForm[propertyType] || '');
      }
    }
  }, [propertyType, value, formattedValue, currentForm, isTyping]);

  useEffect(() => {
    if (!isRequired && setIsErrorForm) {
      setInputError(false);
      setIsErrorForm(false);
    } else if (isRequired && !phoneRegex.test(formattedValue) && value && setIsErrorForm) {
      setInputError(true);
      setIsErrorForm(true);
    }
  }, [isRequired, formattedValue, phoneRegex, setIsErrorForm, value]);

  useEffect(() => {
    if (currentOption === 'Select' && !isPersonalInformation) {
      setInputDisabled(true);
      setFormattedValue('');
    }
  }, [currentOption, isPersonalInformation]);

  // phone type changes
  useEffect(() => {
    if (setTypeOfPhone && currentOption && currentOption !== labelDropDown && !isTyping) {
      const newPhoneType = formatPropertySelected(currentOption);
      if (currentForm[newPhoneType] !== formattedValue) {
        setFormattedValue(currentForm[newPhoneType] || '');
      }
    }
  }, [currentOption, labelDropDown, isTyping, setTypeOfPhone, formatPropertySelected, currentForm, formattedValue]);

  return (
    <Container ref={ref} $isHidden={isHidden} size={size} $hasAsterisk={hasAsterisk}>
      {labelContainer && <Label $hasAsterisk={hasAsterisk}>{label}</Label>}
      <InputGroup>
        <IconContainer $iconColor={iconColor} $isPersonalInformation={isPersonalInformation}>
          <PhoneIcon />
        </IconContainer>
        {!isPersonalInformation && (
          <>
            <DropDownHeader
              $hasIcon={hasIcon}
              $hasError={!!inputError}
              onClick={toggling}
              ref={wrapperRef}
              onKeyDown={handleKeyDown}
              tabIndex={0}
              role="combobox"
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              aria-label={label}
              data-testid={`${dataTestId}-phone-header`}
            >
              <div>{currentOption ? getLabelByBeValue(currentOption) : labelDropDown}</div>
              <DownIcon $isIconUp={isOpen} />
            </DropDownHeader>
            {isOpen && (
              <DropDownListContainer>
                <DropDownList $hasError={!!inputError}>
                  {options?.map((option, index) => (
                    <ListItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onOptionClicked(option.beValue)();
                      }}
                      key={Math.random()}
                      onKeyDown={(event) => {
                        event.stopPropagation();
                        handleOptionKeyDown(event, option.beValue, index);
                      }}
                      data-testid={`${dataTestId}-phone-option-${index}`}
                    >
                      {option.label}
                    </ListItem>
                  ))}
                </DropDownList>
              </DropDownListContainer>
            )}
          </>
        )}
        <Input
          data-testid={dataTestId}
          type={'phone'}
          placeholder={placeHolder}
          value={formattedValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={isPersonalInformation ? false : isInputDisabled}
          $hasError={!!inputError}
          $focused={focused}
          $hasIcon={hasIcon}
          maxLength={maxLength}
          $isPersonalInformation={isPersonalInformation}
          $placeholderColor={placeholderColor}
          {...(shouldApplyMasking && { className: 'nr-block' })}
        />
      </InputGroup>
      {inputError && <ErrorMessage>{'you must provide a valid phone number'}</ErrorMessage>}
    </Container>
  );
};
